export const COLORS = {
  primary: '#007AFF',
};

export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3003/api/v1';

export const API_ENDPOINTS = {
  users: {
    session: '/user/session',
    me: '/user/me',
    update: '/user/update',
  },
  auth: {
    signin: '/auth/signin',
    checkEmailVerification: '/auth/check-email-verification',
    onboardingStatus: '/auth/onboarding/status',
    onboarding: '/auth/onboarding',
    forgotPassword: '/auth/forgot-password',
    confirmResetPassword: '/auth/reset-password/confirm',
    inspectEmailAction: '/auth/email-action/inspect',
    applyEmailAction: '/auth/email-action/apply',
    logout: '/auth/logout',
  },
  patients: {
    create: '/patients/',
    list: '/patients/',
    update: (id: string) => `/patients/${id}`,
    delete: (id: string) => `/patients/${id}`,
    bulkDelete: '/patients/bulk-delete',
  },
  payers: {
    create: '/payers/',
    list: '/payers/',
    update: (id: string) => `/payers/${id}`,
    delete: (id: string) => `/payers/${id}`,
    bulkDelete: '/payers/bulk-delete',
  },
  providers: {
    create: '/providers/',
    list: '/providers/',
    detail: (id: string) => `/providers/${id}`,
    update: (id: string) => `/providers/${id}`,
    delete: (id: string) => `/providers/${id}`,
    bulkDelete: '/providers/bulk-delete',
  },
  nppes: {
    lookup: (npi: string) => `/nppes/lookup/${npi}`,
    adminLookup: (npi: string) => `/admin/nppes/lookup/${npi}`,
  },
  claims: {
    create: '/claims/',
    list: '/claims/',
    detail: (id: string) => `/claims/${id}`,
    update: (id: string) => `/claims/${id}`,
    delete: (id: string) => `/claims/${id}`,
    updateStatus: (id: string) => `/claims/${id}/status`,
    verify: (id: string) => `/claims/${id}/verify`,
    verification: (id: string) => `/claims/${id}/verification`,
    availableActions: (id: string) => `/claims/${id}/available-actions`,
    documents: (id: string) => `/claims/${id}/documents`,
    bulkUpload: '/upload-claims',
    bulkUploadExample: '/upload-claims/example',
    uploadBatches: '/upload-claims/batches',
    uploadBatch: (id: string) => `/upload-claims/batches/${id}`,
    uploadBatchFile: (id: string) => `/upload-claims/batches/${id}/file`,
    uploadBatchMapping: (id: string) => `/upload-claims/batches/${id}/mapping`,
    uploadBatchProcess: (id: string) => `/upload-claims/batches/${id}/process`,
    uploadMappingPresets: '/upload-claims/mapping-presets',
    bulkDelete: '/claims/bulk-delete',
    statusLabels: '/claims/status-labels',
  },
  twilio: {
    aiCallingStatus: '/twilio/ai-calling-status',
    toggleAiCalling: '/twilio/ai-calling',
  },
  calls: {
    list: '/calls/',
    manual: '/calls/manual',
    patient: '/calls/patient',
    reprocess: (id: string) => `/calls/${id}/reprocess`,
    transcript: (id: string) => `/calls/${id}/transcript`,
    end: (id: string) => `/live-calls/${id}/end`,
    adminEnd: (id: string) => `/admin/live-calls/${id}/end`,
  },
  sms: {
    config: '/sms/config',
    send: '/sms/send',
    list: '/sms/',
  },
  scheduling: {
    appointments: '/scheduling/appointments',
    appointment: (id: string) => `/scheduling/appointments/${id}`,
    availability: '/scheduling/availability',
    slots: '/scheduling/slots',
  },
  paymentPosting: {
    remittances: '/payment-posting/remittances',
    fetch: '/payment-posting/fetch',
    postRemittance: (id: string) => `/payment-posting/remittances/${id}/post`,
    postPayment: (id: string) => `/payment-posting/payments/${id}/post`,
  },
  priorAuth: {
    list: '/prior-auth/',
    create: '/prior-auth/',
    detail: (id: string) => `/prior-auth/${id}`,
  },
  dashboard: {
    overview: '/dashboard/overview',
    sidebarCounts: '/dashboard/sidebar-counts',
    insightsResponses: '/dashboard/insights-responses',
  },
  appeals: {
    templates: '/appeals/templates',
    drafts: '/appeals/drafts',
    reviewQueue: '/appeals/review-queue',
    detail: (id: string) => `/appeals/${id}`,
    updateContent: (id: string) => `/appeals/${id}/content`,
    addDocument: (id: string) => `/appeals/${id}/documents`,
    submit: (id: string) => `/appeals/${id}/submit`,
  },
  playbooks: {
    list: '/playbooks',
    stats: '/playbooks/stats',
    detail: (code: string) => `/playbooks/${code}`,
    outcomes: (id: string) => `/playbooks/${id}/outcomes`,
  },
  tools: {
    denialLookup: '/tools/denial-lookup',
    payerRules: '/tools/payer-rules',
    authLookup: '/tools/auth-lookup',
    liveEligibility: '/tools/live-eligibility',
  },
  escalations: {
    create: (callId: string) => `/calls/${callId}/escalation`,
    list: (callId: string) => `/calls/${callId}/escalations`,
  },
  payerReality: {
    list: '/payer-reality/',
  },
  voice: {
    signedUrl: '/voice/signed-url',
    playgroundUrl: '/voice/playground-url',
    playgroundTool: '/playground/tool',
  },
  demo: {
    meeting: '/demo/meeting',
  },
  billing: {
    plans: '/billing/plans',
    subscription: '/billing/subscription',
    subscribe: '/billing/subscribe',
    changePlan: '/billing/change-plan',
    cancel: '/billing/cancel',
    creditPurchase: '/billing/credits/purchase',
    creditBalance: '/billing/credits/balance',
    creditTransactions: '/billing/credits/transactions',
    usage: '/billing/usage',
    invoices: '/billing/invoices',
    paymentMethodSetupIntent: '/billing/payment-methods/setup-intent',
    paymentMethods: '/billing/payment-methods',
    paymentMethodDelete: (id: string) => `/billing/payment-methods/${id}`,
  },
  ehr: {
    connections: '/ehr/connections',
    connectionDelete: (id: string) => `/ehr/connections/${id}`,
    connectionSync: (id: string) => `/ehr/connections/${id}/sync`,
    syncJobs: '/ehr/sync-jobs',
    syncJobDetail: (id: string) => `/ehr/sync-jobs/${id}`,
    eligibilityCheck: '/ehr/eligibility/check',
    claimStatusCheck: '/ehr/claim-status/check',
    delegatedProbe: '/ehr/claim-status/delegated-probe',
    bulkClaimStatusCheck: '/ehr/claim-status/bulk-check',
    transactions: '/ehr/transactions',
    transactionDetail: (id: string) => `/ehr/transactions/${id}`,
    payerNetworkSearch: '/ehr/payer-network/search',
    fieldMappings: '/ehr/field-mappings',
  },
  orgConfig: {
    claimFieldSchema: '/org-config/claim-field-schema',
  },
  workflow: {
    /** Relative to API_BASE_URL; playgroundSocket.ts prepends the base and swaps to wss. */
    playgroundWsPath: '/workflow/playground/ws',
    orgConfig: (orgId: string) => `/workflow/orgs/${orgId}/config`,
    playgroundPostCallAnalyze: '/workflow/playground/post-call-analyze',
  },
};
